import type { UserModel } from './_models'
import type { ResolvedPermission } from './_models'
import { isPlatformSuperAdmin } from './platformSuperAdmin'

function normalizePerm(code: string): string {
  return (code || '').trim().toUpperCase()
}

type PermissionLikeObject = Record<string, any>

function asPermissionObject(value: unknown): PermissionLikeObject | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as PermissionLikeObject) : null
}

function extractPermissionCodeFromObject(value: PermissionLikeObject): string | null {
  const nestedPermission = asPermissionObject(value.permission)
  const raw =
    value.permissionCode ??
    value.name ??
    value.code ??
    nestedPermission?.name ??
    nestedPermission?.permissionCode ??
    nestedPermission?.code

  return typeof raw === 'string' ? normalizePerm(raw) || null : null
}

function looksLikeResolvedDecision(value: PermissionLikeObject): boolean {
  return (
    Object.prototype.hasOwnProperty.call(value, 'allowed') ||
    Object.prototype.hasOwnProperty.call(value, 'scope') ||
    Object.prototype.hasOwnProperty.call(value, 'source')
  )
}

function normalizeDecisionValue(
  value: unknown,
  fallback: Partial<ResolvedPermission> = {}
): ResolvedPermission {
  const obj = asPermissionObject(value)
  return {
    allowed:
      typeof obj?.allowed === 'boolean'
        ? obj.allowed
        : typeof fallback.allowed === 'boolean'
          ? fallback.allowed
          : true,
    scope:
      obj?.scope == null
        ? (fallback.scope ?? null)
        : String(obj.scope).trim() || null,
    source:
      typeof obj?.source === 'string' && obj.source.trim()
        ? obj.source.trim().toUpperCase()
        : String(fallback.source || 'ROLE').trim().toUpperCase(),
  }
}

export function normalizePermissionCode(item: unknown): string | null {
  if (!item) return null

  if (typeof item === 'string') {
    const normalized = normalizePerm(item)
    return normalized || null
  }

  const obj = asPermissionObject(item)
  if (!obj) return null

  return extractPermissionCodeFromObject(obj)
}

export function normalizeResolvedPermissionMap(rawPermissions: unknown): Record<string, ResolvedPermission> {
  const out: Record<string, ResolvedPermission> = {}

  if (!rawPermissions) return out

  if (Array.isArray(rawPermissions)) {
    rawPermissions.forEach((entry) => {
      const code = normalizePermissionCode(entry)
      if (!code) return
      out[code] = normalizeDecisionValue(entry)
    })
    return out
  }

  const directObject = asPermissionObject(rawPermissions)
  if (!directObject) return out

  const directCode = extractPermissionCodeFromObject(directObject)
  if (directCode) {
    out[directCode] = normalizeDecisionValue(directObject)
    return out
  }

  Object.entries(directObject).forEach(([rawKey, rawValue]) => {
    const keyCode = normalizePerm(rawKey)
    if (!keyCode) return

    const valueObject = asPermissionObject(rawValue)
    if (valueObject && looksLikeResolvedDecision(valueObject)) {
      out[keyCode] = normalizeDecisionValue(valueObject, { allowed: false })
      return
    }

    const valueCode = normalizePermissionCode(rawValue)
    if (valueCode && valueCode !== keyCode) {
      out[valueCode] = normalizeDecisionValue(rawValue)
      return
    }

    out[keyCode] = normalizeDecisionValue(rawValue, { allowed: Boolean(rawValue) })
  })

  return out
}

export function buildAllowedPermissionSet(rawPermissions: unknown): Set<string> {
  return new Set(
    Object.entries(normalizeResolvedPermissionMap(rawPermissions))
      .filter(([, decision]) => decision.allowed)
      .map(([code]) => code)
  )
}

function toSet(perms: any): Set<string> {
  if (!perms) return new Set()
  if (perms instanceof Set) {
    return new Set(Array.from(perms, normalizePerm).filter(Boolean))
  }
  if (Array.isArray(perms)) {
    return new Set(perms.map(normalizePermissionCode).filter((value): value is string => Boolean(value)))
  }
  if (typeof perms === 'object') {
    return buildAllowedPermissionSet(perms)
  }
  return new Set()
}

type EffectivePermissionMap = Record<string, ResolvedPermission | { allowed?: boolean } | undefined>

function toPermissionMap(perms?: EffectivePermissionMap | null): Record<string, { allowed: boolean }> {
  return Object.fromEntries(
    Object.entries(normalizeResolvedPermissionMap(perms)).map(([code, decision]) => [
      code,
      { allowed: decision.allowed === true },
    ])
  )
}

function collectSuperAdminSignals(user?: Pick<UserModel, 'role'> | null): unknown[] {
  if (!user) return []
  const u = user as Record<string, unknown>
  const out: unknown[] = [user, u.role]
  return out
}

/**
 * Platform super-admin: use the same normalization as {@link isPlatformSuperAdmin} plus explicit enum
 * {@code SUPER_ADMIN} on {@code role.roleType}. Kept here so all permission helpers share one definition.
 */
export function isSuperAdminUser(user?: Pick<UserModel, 'role'> | null): boolean {
  if (!user) return false
  const t = String(user.role?.roleType || '').toUpperCase()
  if (t === 'SUPER_ADMIN') return true
  const signals = collectSuperAdminSignals(user)
  return signals.some((s) => isPlatformSuperAdmin(s))
}

export function hasPermission(
  effectivePermissions:
    | Set<string>
    | string[]
    | EffectivePermissionMap
    | undefined
    | null,
  code: string
): boolean {
  const c = normalizePerm(code)
  if (!c) return false
  if (effectivePermissions && !(effectivePermissions instanceof Set) && !Array.isArray(effectivePermissions)) {
    const map = toPermissionMap(effectivePermissions)
    return map[c]?.allowed === true
  }
  return toSet(effectivePermissions).has(c)
}

export function hasAnyPermission(
  effectivePermissions:
    | Set<string>
    | string[]
    | EffectivePermissionMap
    | undefined
    | null,
  codes: string[]
): boolean {
  if (!codes?.length) return true
  if (effectivePermissions && !(effectivePermissions instanceof Set) && !Array.isArray(effectivePermissions)) {
    const map = toPermissionMap(effectivePermissions)
    return codes.some((x) => map[normalizePerm(x)]?.allowed === true)
  }
  const set = toSet(effectivePermissions)
  return codes.some((x) => set.has(normalizePerm(x)))
}

export function hasAllPermissions(
  effectivePermissions:
    | Set<string>
    | string[]
    | EffectivePermissionMap
    | undefined
    | null,
  codes: string[]
): boolean {
  if (!codes?.length) return true
  if (effectivePermissions && !(effectivePermissions instanceof Set) && !Array.isArray(effectivePermissions)) {
    const map = toPermissionMap(effectivePermissions)
    return codes.every((x) => map[normalizePerm(x)]?.allowed === true)
  }
  const set = toSet(effectivePermissions)
  return codes.every((x) => set.has(normalizePerm(x)))
}
